import { useEffect, useState } from 'react';

import YouTube, { type YouTubePlayer, type YouTubeProps } from 'react-youtube';

import { useAppDispatch } from 'dux/app/hooks';

import { styled } from '@prose-ui/legacy';
import { useOnKeydownListener } from 'hooks/useOnKeydownListener';
import Image from 'next/image';

import Typography from 'Components/Typography';

import closeIcon from 'assets/images/icon-close-white.svg';

import { trackHeapEvent } from 'dux/tracking/actions';

const Modal = styled.div<{ isVideoOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9999;
  outline: 0;
  padding: 0;

  display: ${({ isVideoOpen }) => (isVideoOpen ? 'block' : 'none')};
`;

const CloseButton = styled(Typography)`
  position: fixed;
  top: 60px;
  right: 28px;
  color: white;
  cursor: pointer;
`;

type HowItWorksModalProps = {
  onClose: () => void;
  isVideoOpen: boolean;
};

const videoId = 'EDnG9Zqyso8';

export const HowItWorksModal = ({ onClose, isVideoOpen }: HowItWorksModalProps) => {
  const dispatch = useAppDispatch();
  useOnKeydownListener({ onKeyDown: onClose, key: 'Escape' });

  const [videoTarget, setVideoTarget] = useState<null | YouTubePlayer>(null);

  const onPlay = () => {
    dispatch(trackHeapEvent('Video play'));
  };

  const onPause = () => {
    dispatch(trackHeapEvent('Video paused'));
  };

  const handleClose = () => {
    if (videoTarget) {
      videoTarget.stopVideo();
    }
    dispatch(trackHeapEvent('YouTube iframe close'));
    onClose();
  };

  const onEnd = () => {
    dispatch(trackHeapEvent('Video ended'));
    handleClose();
  };

  useEffect(() => {
    if (isVideoOpen) {
      dispatch(trackHeapEvent('YouTube iframe open'));

      if (videoTarget) {
        videoTarget.playVideo();
      }
    }
  }, [isVideoOpen]);

  const onReady: YouTubeProps['onReady'] = (event) => {
    setVideoTarget(event.target);
  };

  return (
    <Modal data-testid="how-it-works-video-modal" isVideoOpen={isVideoOpen}>
      {/* @ts-ignore Legacy Typography  */}
      <CloseButton
        data-click="exit"
        data-from="youtube-iframe-modal"
        data-testid="youtube-iframe-close-button"
        onClick={handleClose}
        variant="h1"
      >
        <Image alt="Close youtube video button" height={16} src={closeIcon} width={16} />
      </CloseButton>

      <YouTube
        onEnd={onEnd}
        onPause={onPause}
        onPlay={onPlay}
        onReady={onReady}
        opts={{
          height: '100%',
          width: '100%',
          playerVars: { autoplay: 0, rel: 0, cc_load_policy: 1, playsinline: 1 },
        }}
        style={{ height: '100%', width: '100%' }}
        title="YouTube video player"
        videoId={videoId}
      />
    </Modal>
  );
};
